import React, { useEffect, useState } from 'react';
import { bool, func, number, string } from 'prop-types';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';
import classNames from 'classnames';
import { numberAtLeast, required, negotiationPriceValidator, requiredFieldArrayCheckbox } from '../../../util/validators';
import {
  PURCHASE_PROCESS_NAME,
  PURCHASE_NEGOTIATION_PROCESS_NAME,
} from '../../../transactions/transaction';
import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';

import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  H3,
  H6,
  FieldCurrencyInput,
  FieldCheckbox,
  FieldRadioButton,
  IconOffer,
  Heading,
  IconBuy,
  H4,
  FieldCheckboxGroup,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import css from './ProductOrderForm.module.css';
import includes from 'lodash/includes';
import { SecondaryButton } from '../../Button/Button';
import IconSpinner from '../../IconSpinner/IconSpinner';
const { Money } = sdkTypes;
// Browsers can't render huge number of select options.
// (stock is shown inside select element)
// Note: input element could allow ordering bigger quantities
const MAX_QUANTITY_FOR_DROPDOWN = 100;

const handleFetchLineItems = ({
  quantity,
  deliveryMethod,
  negotiatedTotal,
  displayDeliveryMethod,
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  shipment,
}) => {
  const negotiatedTotalMaybe = !!negotiatedTotal ? { negotiatedTotal } : null;
  const stockReservationQuantity = Number.parseInt(quantity, 10);
  const isBrowser = typeof window !== 'undefined';
  const shipmentMaybe = !!shipment ? { shipment } : {};
  const protectedDataMaybe = {
    protectedData: {
      ...shipmentMaybe,
    },
  };
  if (
    isBrowser &&
    stockReservationQuantity &&
    (!displayDeliveryMethod || deliveryMethod) &&
    !fetchLineItemsInProgress
  ) {
    onFetchTransactionLineItems({
      orderData: {
        stockReservationQuantity,
        deliveryMethod,
        ...negotiatedTotalMaybe,
        ...protectedDataMaybe,
      },
      listingId,
      isOwnListing,
    });
  }
};

const DeliveryMethodMaybe = props => {
  const {
    displayDeliveryMethod,
    hasMultipleDeliveryMethods,
    deliveryMethod,
    hasStock,
    formId,
    intl,
    pickupEnabled,
    shippingEnabled,
    dynamicShippingEnabled,
  } = props;
  const showDeliveryMethodSelector = displayDeliveryMethod && hasMultipleDeliveryMethods;
  const showSingleDeliveryMethod = displayDeliveryMethod && deliveryMethod;
  return !hasStock ? null : showDeliveryMethodSelector ? (
    <FieldSelect
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      label={intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      validate={required(intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodRequired' }))}
    >
      <option disabled value="">
        {intl.formatMessage({ id: 'ProductOrderForm.selectDeliveryMethodOption' })}
      </option>
      {pickupEnabled ? (
        <option value={'pickup'}>
          {intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
        </option>
      ) : null}
      {shippingEnabled ? (
        <option value={'shipping'}>
          {intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })}
        </option>
      ) : null}
      {dynamicShippingEnabled ? (
        <option value={'dynamicShipping'}>
          {intl.formatMessage({ id: 'ProductOrderForm.dynamicShippingOption' })}
        </option>
      ) : null}
    </FieldSelect>
  ) : showSingleDeliveryMethod ? (
    <div className={css.deliveryField}>
      <H3 rootClassName={css.singleDeliveryMethodLabel}>
        {intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      </H3>
      <p className={css.singleDeliveryMethodSelected}>
        {deliveryMethod === 'shipping'
          ? intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })
          : deliveryMethod === 'dynamicShipping'
            ? intl.formatMessage({ id: 'ProductOrderForm.dynamicShippingOption' })
            : intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </p>
      <FieldTextInput
        id={`${formId}.deliveryMethod`}
        className={css.deliveryField}
        name="deliveryMethod"
        type="hidden"
      />
    </div>
  ) : (
    <FieldTextInput
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      type="hidden"
    />
  );
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    allowOrdersOfMultipleItems,
    hasMultipleDeliveryMethods,
    pickupEnabled,
    shippingEnabled,
    dynamicShippingEnabled,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    shipping,
    pristine,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    payoutDetailsWarning,
    shippingDetailsWarning,
    marketplaceName,
    values,
    processName,
    shippingCountryName,
    initiateOrderInProgress,
  } = formRenderProps;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    const { quantity, deliveryMethod, negotiatedTotal } = values;
    if (quantity && !formRenderProps.hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        negotiatedTotal,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
      });
    }
  }, []);

  // If form values change, update line-items for the order breakdown
  const handleOnChange = (formValues) => {
    const { quantity, deliveryMethod, negotiatedTotal, shippingRateId, termsToProviderPmAndShippingDetails } = formValues.values || {};
    const isDynamicShipping = deliveryMethod === 'dynamicShipping';
    const isMakeOfferSelected = values?.orderType === 'offer';
    const selectedShipping = shipping?.rates?.find(r => r?.servicelevel?.token === shippingRateId);
    const shipmentMaybe = !!selectedShipping
      ? {
        shipment: {
          id: shipping?.id,
          rate: {
            id: selectedShipping.object_id,
            carrierAccount: selectedShipping.carrier_account,
            amount: selectedShipping.amount_local || selectedShipping.amount,
            currency: selectedShipping.currency_local || selectedShipping.currency,
            estimatedDays: selectedShipping.estimated_days,
            serviceLevel: { ...selectedShipping.servicelevel },
          },
          shipmentDate: shipping?.shipmentDate,
        },
      }
      : {};
    let hasValidData = false;
    if (isDynamicShipping) {
      hasValidData = mounted && !!quantity && !!deliveryMethod && !selectedShipping;
    }
    else {
      hasValidData = mounted && !!quantity && !!deliveryMethod && ((isMakeOfferSelected && !!termsToProviderPmAndShippingDetails?.[0]) || !isMakeOfferSelected);
    }
    const negotiatedTotalMaybe = isMakeOfferSelected && !!negotiatedTotal ? { negotiatedTotal } : null;
    if (hasValidData) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        ...negotiatedTotalMaybe,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        ...shipmentMaybe,
      });
    }
  };

  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity, deliveryMethod, orderType, shippingRateId } = values || {};
    const isDynamicShipping = deliveryMethod === 'dynamicShipping';
    const isMakeOfferSelected = orderType === 'offer';
    if (!quantity || quantity < 1) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (displayDeliveryMethod && !deliveryMethod) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else if (isDynamicShipping && isMakeOfferSelected && !shippingRateId) {
      e.preventDefault();
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const isShipping =  values?.deliveryMethod === 'shipping';
  const isDynamicShipping = values?.deliveryMethod === 'dynamicShipping';
  const breakdownData = {}
  const showBreakdown =  !isDynamicShipping && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });

  // Listing is out of stock if currentStock is zero.
  // Undefined/null stock means that stock has never been set.
  const hasNoStockLeft = typeof currentStock != null && currentStock === 0;
  const hasStock = currentStock && currentStock > 0;
  const hasOneItemLeft = currentStock === 1;
  const selectableStock =
    currentStock > MAX_QUANTITY_FOR_DROPDOWN ? MAX_QUANTITY_FOR_DROPDOWN : currentStock;
  const quantities = hasStock ? [...Array(selectableStock).keys()].map(i => i + 1) : [];

  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled =
    !hasStock || !!submitInProgress || ((isDynamicShipping || isShipping) && !!shippingDetailsWarning);
  const config = useConfiguration();
  const isMakeOfferSelected = values?.orderType === 'offer';
  const showAsRequired = pristine;
  
  const internationalShippingNote =
    !isOwnListing && isShipping || isDynamicShipping ? (
      <p className={css.internationalShippingNote}>
        {!!shippingCountryName ? (
          <FormattedMessage
            id="ListingPage.internationalCountryShippingNote"
            values={{ country: shippingCountryName }}
          />
        ) : (
          <FormattedMessage id="ListingPage.internationalShippingNote" />
        )}
      </p>
    ) : null;

  return (
    <Form onSubmit={handleFormSubmit}>
      {
        !isDynamicShipping  && <FormSpy subscription={{ values: true }} onChange={handleOnChange} />
      }

      <div className={css.orderOptions}>
        <FieldRadioButton
          id={`${formId}-orderType-purchase`}
          name="orderType"
          className={classNames(css.orderOption, { [css.orderOptionSelected]: !isMakeOfferSelected })}
          label={intl.formatMessage({ id: 'ProductOrderForm.buyNow' })}
          value="purchase"
          showAsRequired={showAsRequired}
        />

        <FieldRadioButton
          id={`${formId}-orderType-offer`}
          name="orderType"
          className={classNames(css.orderOption, { [css.orderOptionSelected]: isMakeOfferSelected })}
          label={intl.formatMessage({ id: 'ProductOrderForm.offer' })}
          value="offer"
          showAsRequired={showAsRequired}
        />
      </div>

      {hasNoStockLeft ? null : hasOneItemLeft || !allowOrdersOfMultipleItems ? (
        <FieldTextInput
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          type="hidden"
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        />
      ) : (
        <FieldSelect
          id={`${formId}.quantity`}
          className={css.quantityField}
          name="quantity"
          disabled={!hasStock}
          label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel' })}
          validate={numberAtLeast(quantityRequiredMsg, 1)}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ProductOrderForm.selectQuantityOption' })}
          </option>
          {quantities.map(quantity => (
            <option key={quantity} value={quantity}>
              {intl.formatMessage({ id: 'ProductOrderForm.quantityOption' }, { quantity })}
            </option>
          ))}
        </FieldSelect>
      )}

      {processName === PURCHASE_NEGOTIATION_PROCESS_NAME && isMakeOfferSelected ? (
        <div className={css.offerWrapper}>
          <FieldCurrencyInput
            id={`${formId}-negotiatedTotal`}
            name="negotiatedTotal"
            className={css.negotiatedTotal}
            label={intl.formatMessage({ id: 'ProductOrderForm.negotiatedTotal' })}
            placeholder={intl.formatMessage({
              id: 'ProductOrderForm.negotiatedTotalPlaceholder',
            })}
            currencyConfig={appSettings.getCurrencyFormatting(config.currency)}
            validate={!!isMakeOfferSelected ? negotiationPriceValidator(
              intl.formatMessage({
                id: 'ProductOrderForm.negotiationPriceInvalid',
              })
            ) : null}
          />
        </div>
      ) : null}

      <div className={css.deliveryMethodWrapper}>
        <DeliveryMethodMaybe
          displayDeliveryMethod={displayDeliveryMethod}
          hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
          deliveryMethod={values?.deliveryMethod}
          hasStock={hasStock}
          formId={formId}
          intl={intl}
          pickupEnabled={pickupEnabled}
          shippingEnabled={shippingEnabled}
          dynamicShippingEnabled={dynamicShippingEnabled}
        />

        {
          isMakeOfferSelected && isDynamicShipping ?
            <div className={css.addShipping} onClick={() => handleOnChange({values: values})}>
              {intl.formatMessage({ id: 'ProductOrderForm.chooseShipping' })}
            </div> : null
        }
      </div>


      {isMakeOfferSelected && shipping?.rates && shipping?.rates?.length > 0 ? (
        <div className={css.shippingRates}>
          <H4 className={css.chooseShipping}>
            <FormattedMessage id="StripePaymentForm.chooseShipping" />
          </H4>
          {shipping?.rates.map(rate => {
            const amount = new Money(rate.amount * 100, rate.currency);
            const formattedAmount = formatMoney(intl, amount);
            const label = (
              <div className={css.rate}>
                <div className={css.rateProvider}>
                  <span>
                    <img src={rate?.provider_image_200} alt={rate?.servicelevel?.name} />
                  </span>
                  <span>
                    {intl.formatMessage(
                      { id: 'ProductOrderForm.shippingRate' },
                      {
                        shippingProvider: rate?.provider,
                        name: rate?.servicelevel?.name,
                      }
                    )}
                  </span>
                </div>
                <div className={css.rateAmount}>{formattedAmount}</div>
              </div>
            );
            return (
              <FieldRadioButton
                id={`${formId}-${rate.object_id}`}
                name="shippingRateId"
                label={label}
                value={rate?.servicelevel?.token}
                showAsRequired={showAsRequired}
              />
            );
          })}
        </div>
      ) : submitInProgress ? (
        <div className={css.spinner}>
          <IconSpinner />
        </div>
      ) : null}

      <div className={css.terms}>
        {
          isMakeOfferSelected && <FieldCheckboxGroup
            name="termsToProviderPmAndShippingDetails"
            id="termsToProviderPmAndShippingDetails"
            optionLabelClassName={css.termsLabel}
            options={[
              {
                key: 'termsToProviderPmAndShippingDetails',
                label: intl.formatMessage(
                  { id: 'ProductOrderForm.termsToProviderPmAndShippingDetails' },
                ),
              },
            ]}
            validate={requiredFieldArrayCheckbox(
              intl.formatMessage({ id: 'ProductOrderForm.termsToProviderPmAndShippingDetailsRequired' })
            )}
          />
        }
      </div>

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={breakdownData}
            lineItems={lineItems}
            currency={price.currency}
            marketplaceName={marketplaceName}
            processName={PURCHASE_PROCESS_NAME}
          />
        </div>
      ) : null}

      <div className={css.submitButton}>
        {isMakeOfferSelected && <PrimaryButton
          className={css.makeOffer}
          type="submit"
          disabled={submitDisabled || !isMakeOfferSelected || isDynamicShipping && isMakeOfferSelected && !values?.shippingRateId || initiateOrderInProgress}
          inProgress={initiateOrderInProgress}
        >
          {/* <span className={css.offerIcon}>
            <IconOffer />
          </span> */}
          <span>
            <FormattedMessage id="ProductOrderForm.makeOffer" />
          </span>
        </PrimaryButton>}
        {
          !isMakeOfferSelected &&
          <PrimaryButton type="submit" disabled={submitDisabled || isMakeOfferSelected}>
            <FormattedMessage id="ProductOrderForm.ctaButton" />
          </PrimaryButton>
        }
      </div>
      <p className={classNames(css.finePrint, { [css.finePrintNoStock]: !hasStock })}>
        {(isDynamicShipping || isShipping) && shippingDetailsWarning ? (
          shippingDetailsWarning
        ) : payoutDetailsWarning ? (
          payoutDetailsWarning
        ) : hasStock && isOwnListing ? (
          <FormattedMessage id="ProductOrderForm.ownListing" />
        ) : hasStock ? (
          <FormattedMessage id="ProductOrderForm.finePrint" />
        ) : showContactUser ? (
          <FormattedMessage id="ProductOrderForm.finePrintNoStock" values={{ contactSellerLink }} />
        ) : null}
      </p>
      {internationalShippingNote}
    </Form>
  );
};

const ProductOrderForm = props => {
  const intl = useIntl();
  const {
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    dynamicShippingEnabled,
    displayDeliveryMethod,
    allowOrdersOfMultipleItems,
  } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  if (displayDeliveryMethod && !pickupEnabled && !shippingEnabled && !dynamicShippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const hasOneItemLeft = currentStock && currentStock === 1;
  const hasOneItemMode = !allowOrdersOfMultipleItems && currentStock > 0;
  const quantityMaybe = hasOneItemLeft || hasOneItemMode ? { quantity: '1' } : {};
  const deliveryMethodMaybe = shippingEnabled
    ? { deliveryMethod: 'shipping' }
    : pickupEnabled
      ? { deliveryMethod: 'pickup' }
      : dynamicShippingEnabled
        ? { deliveryMethod: 'dynamicShipping' }
        : !shippingEnabled && !pickupEnabled && !dynamicShippingEnabled
          ? { deliveryMethod: 'none' }
          : {};
  const hasMultipleDeliveryMethods =
    [pickupEnabled, shippingEnabled, dynamicShippingEnabled].filter(s => !!s)?.length > 1;
  const initialValues = { ...quantityMaybe, ...deliveryMethodMaybe, orderType: 'purchase' };

  return (
    <FinalForm
      keepDirtyOnReinitialize
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      pickupEnabled={pickupEnabled}
      shippingEnabled={shippingEnabled}
      dynamicShippingEnabled={dynamicShippingEnabled}
      displayDeliveryMethod={displayDeliveryMethod}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

ProductOrderForm.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  currentStock: null,
  listingId: null,
  isOwnListing: false,
  pickupEnabled: false,
  shippingEnabled: false,
  displayDeliveryMethod: false,
  lineItems: null,
  fetchLineItemsError: null,
};

ProductOrderForm.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,

  // form
  formId: string.isRequired,
  onSubmit: func.isRequired,

  // listing
  listingId: propTypes.uuid,
  price: propTypes.money,
  currentStock: number,
  isOwnListing: bool,
  pickupEnabled: bool,
  shippingEnabled: bool,
  displayDeliveryMethod: bool,

  // line items
  lineItems: propTypes.lineItems,
  onFetchTransactionLineItems: func.isRequired,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // other
  onContactUser: func,
};

export default ProductOrderForm;